import { type ChangeEvent } from "react";
import { type ChurchFormData } from "../types";

interface Props {
  data: ChurchFormData;
  onChange: (field: keyof ChurchFormData, value: unknown) => void;
  errors?: Partial<Record<keyof ChurchFormData, string>>;
}

const DIOCESES = [
  "Archidiocèse de Dakar",
  "Diocèse de Thiès",
  "Diocèse de Kaolack",
  "Diocèse de Ziguinchor",
  "Diocèse de Saint-Louis",
  "Diocèse de Tambacounda",
  "Diocèse de Kolda",
];

export default function StepBasicInfo({ data, onChange, errors = {} }: Props) {
  const handleInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    onChange(e.target.name as keyof ChurchFormData, e.target.value);
  };

  const handleImageFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    onChange("imageFile", file);
    onChange("image", file ? URL.createObjectURL(file) : "");
  };

  const handleVideoFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    onChange("videoFile", file);
    onChange("video", file ? URL.createObjectURL(file) : "");
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Nom */}
      <div>
        <label className="block text-xs font-bold text-stone-500 mb-1.5 uppercase tracking-wide" style={{ fontFamily: "'Nunito', sans-serif" }}>
          Nom de l&apos;église *
        </label>
        <input
          type="text"
          name="name"
          value={data.name}
          onChange={handleInput}
          placeholder="ex: Paroisse Saint-Pierre de Dakar"
          className={`w-full px-4 py-3 bg-stone-50 border rounded-xl text-sm text-stone-800 outline-none transition-colors ${errors.name ? 'border-red-300 focus:border-red-400' : 'border-stone-200 focus:border-navy-500'}`}
          style={{ fontFamily: "'Nunito', sans-serif" }}
        />
        {errors.name ? <p className="mt-1 text-xs font-semibold text-red-500" style={{ fontFamily: "'Nunito', sans-serif" }}>{errors.name}</p> : null}
      </div>

      {/* Diocèse */}
      <div>
        <label className="block text-xs font-bold text-stone-500 mb-1.5 uppercase tracking-wide" style={{ fontFamily: "'Nunito', sans-serif" }}>
          Diocèse *
        </label>
        <div className="relative">
          <select
            name="diocese"
            value={data.diocese}
            onChange={handleInput}
            className={`w-full px-4 py-3 bg-stone-50 border rounded-xl text-sm text-stone-800 outline-none appearance-none cursor-pointer transition-colors ${errors.diocese ? 'border-red-300 focus:border-red-400' : 'border-stone-200 focus:border-navy-500'}`}
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            <option value="">Choisir un diocèse</option>
            {DIOCESES.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-5 h-5 flex items-center justify-center">
            <i className="ri-arrow-down-s-line text-stone-400 text-lg" />
          </div>
        </div>
        {errors.diocese ? <p className="mt-1 text-xs font-semibold text-red-500" style={{ fontFamily: "'Nunito', sans-serif" }}>{errors.diocese}</p> : null}
      </div>

      {/* Ville */}
      <div>
        <label className="block text-xs font-bold text-stone-500 mb-1.5 uppercase tracking-wide" style={{ fontFamily: "'Nunito', sans-serif" }}>
          Ville *
        </label>
        <input
          type="text"
          name="city"
          value={data.city}
          onChange={handleInput}
          placeholder="ex: Dakar"
          className={`w-full px-4 py-3 bg-stone-50 border rounded-xl text-sm text-stone-800 outline-none transition-colors ${errors.city ? 'border-red-300 focus:border-red-400' : 'border-stone-200 focus:border-navy-500'}`}
          style={{ fontFamily: "'Nunito', sans-serif" }}
        />
        {errors.city ? <p className="mt-1 text-xs font-semibold text-red-500" style={{ fontFamily: "'Nunito', sans-serif" }}>{errors.city}</p> : null}
      </div>

      {/* Adresse */}
      <div>
        <label className="block text-xs font-bold text-stone-500 mb-1.5 uppercase tracking-wide" style={{ fontFamily: "'Nunito', sans-serif" }}>
          Adresse complète *
        </label>
        <input
          type="text"
          name="address"
          value={data.address}
          onChange={handleInput}
          placeholder="ex: Avenue Jean XXIII, Plateau, Dakar"
          className={`w-full px-4 py-3 bg-stone-50 border rounded-xl text-sm text-stone-800 outline-none transition-colors ${errors.address ? 'border-red-300 focus:border-red-400' : 'border-stone-200 focus:border-navy-500'}`}
          style={{ fontFamily: "'Nunito', sans-serif" }}
        />
        {errors.address ? <p className="mt-1 text-xs font-semibold text-red-500" style={{ fontFamily: "'Nunito', sans-serif" }}>{errors.address}</p> : null}
      </div>

      {/* Description */}
      <div>
        <label className="block text-xs font-bold text-stone-500 mb-1.5 uppercase tracking-wide" style={{ fontFamily: "'Nunito', sans-serif" }}>
          Description
        </label>
        <textarea
          name="description"
          value={data.description}
          onChange={handleInput}
          rows={4}
          maxLength={500}
          placeholder="Décrivez brièvement l'histoire et la vie de cette paroisse..."
          className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl text-sm text-stone-800 outline-none focus:border-navy-500 transition-colors resize-none"
          style={{ fontFamily: "'Nunito', sans-serif" }}
        />
        <p className="text-xs text-stone-400 mt-1 text-right" style={{ fontFamily: "'Nunito', sans-serif" }}>
          {data.description.length}/500
        </p>
      </div>

      {/* Image upload */}
      <div>
        <label className="block text-xs font-bold text-stone-500 mb-1.5 uppercase tracking-wide" style={{ fontFamily: "'Nunito', sans-serif" }}>
          Image principale
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageFile}
          className={`w-full px-4 py-3 bg-stone-50 border rounded-xl text-sm text-stone-800 outline-none transition-colors ${errors.imageFile ? 'border-red-300 focus:border-red-400' : 'border-stone-200 focus:border-navy-500'}`}
          style={{ fontFamily: "'Nunito', sans-serif" }}
        />
        {data.image && (
          <div className="mt-2 w-full h-32 rounded-xl overflow-hidden border border-stone-200">
            <img src={data.image} alt="Aperçu" className="w-full h-full object-cover object-top" />
          </div>
        )}
        {errors.imageFile ? <p className="mt-1 text-xs font-semibold text-red-500" style={{ fontFamily: "'Nunito', sans-serif" }}>{errors.imageFile}</p> : null}
        <p className="text-xs text-stone-400 mt-1" style={{ fontFamily: "'Nunito', sans-serif" }}>
          Téléversez une image depuis votre appareil.
        </p>
      </div>

      {/* Video upload */}
      <div>
        <label className="block text-xs font-bold text-stone-500 mb-1.5 uppercase tracking-wide" style={{ fontFamily: "'Nunito', sans-serif" }}>
          Vidéo <span className="text-stone-300 normal-case font-medium">(optionnel)</span>
        </label>
        <input
          type="file"
          accept="video/mp4,video/quicktime,video/webm,video/x-msvideo"
          onChange={handleVideoFile}
          className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl text-sm text-stone-800 outline-none focus:border-navy-500 transition-colors"
          style={{ fontFamily: "'Nunito', sans-serif" }}
        />
        {data.video && (
          <div className="mt-2 overflow-hidden rounded-xl border border-stone-200 bg-black">
            <video src={data.video} controls className="h-48 w-full object-cover" />
          </div>
        )}
        <p className="text-xs text-stone-400 mt-1" style={{ fontFamily: "'Nunito', sans-serif" }}>
          Vous pouvez joindre une courte vidéo de présentation. Ce champ est optionnel.
        </p>
      </div>
    </div>
  );
}
